import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, FileText, Pencil, Plus, Trash2 } from "lucide-react";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  category: string | null;
  status: "draft" | "published";
  authorId: string;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BlogPostsResponse {
  posts: BlogPost[];
  total: number;
  page: number;
  pageSize: number;
}

async function fetchPosts(statusFilter: string, page: number): Promise<BlogPostsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (statusFilter !== "all") {
    params.set("status", statusFilter);
  }

  const res = await fetch(`/api/admin/blog-posts?${params.toString()}`, { credentials: "include" });
  if (!res.ok) {
    throw new Error("Failed to fetch posts");
  }

  return res.json();
}

async function deletePost(id: string): Promise<void> {
  const res = await fetch(`/api/admin/blog-posts/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || "Failed to delete post");
  }
}

function StatusBadge({ status }: { status: BlogPost["status"] }) {
  const styles = {
    draft: "bg-amber-50 text-amber-600",
    published: "bg-emerald-50 text-emerald-600",
  };

  const dots = {
    draft: "bg-amber-500",
    published: "bg-emerald-500",
  };

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${styles[status]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status]}`} />
      {status === "draft" ? "Draft" : "Published"}
    </span>
  );
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export default function Blog() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const canManageBlog = user?.role === "admin" || user?.role === "platform_assistant";

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/blog-posts", statusFilter, page],
    queryFn: () => fetchPosts(statusFilter, page),
    enabled: canManageBlog,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog-posts"] });
      toast({ title: "Post deleted" });
      setConfirmDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    },
  });

  if (!canManageBlog) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md overflow-hidden rounded-3xl bg-card shadow-sm outline outline-1 outline-offset-[-1px] outline-border">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">Access Denied</h3>
            <p className="mt-1 text-sm text-slate-500">You do not have permission to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  const posts = data?.posts ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">Blog Posts</h1>
          <p className="mt-2 text-slate-500">Manage blog content for the landing page.</p>
        </div>
        <Button className="rounded-2xl" onClick={() => navigate("/blog/new")}>
          <Plus className="mr-1.5 h-4 w-4" />
          New Post
        </Button>
      </div>

      <div className="flex gap-4">
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px] rounded-2xl bg-slate-50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-3xl bg-card shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border">
        <div className="border-b border-border/30 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-[43px] w-[43px] items-center justify-center rounded-2xl bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {total} Post{total !== 1 ? "s" : ""}
            </h3>
          </div>
        </div>
        <div className="space-y-4 p-6">
          {posts.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <FileText className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>No posts yet. Create your first post.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wide text-slate-500">Title</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wide text-slate-500">Category</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wide text-slate-500">Status</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wide text-slate-500">Date</TableHead>
                    <TableHead className="text-right text-xs font-bold uppercase tracking-wide text-slate-500">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((post) => (
                    <TableRow key={post.id} className="border-t border-slate-100">
                      <TableCell className="max-w-[280px] truncate font-semibold text-slate-900">{post.title}</TableCell>
                      <TableCell className="text-sm text-slate-500">{post.category ?? "-"}</TableCell>
                      <TableCell>
                        <StatusBadge status={post.status} />
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(post.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl"
                            title="Preview"
                            onClick={() => window.open(`/blog/${post.slug}`, "_blank", "noopener,noreferrer")}
                          >
                            <Eye className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => navigate(`/blog/${post.slug}/edit`)}
                          >
                            <Pencil className="h-4 w-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setConfirmDeleteId(post.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 ? (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDeleteId(null);
          }
        }}
      >
        <DialogContent className="rounded-3xl sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Delete Post?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">This action is permanent and cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-2xl" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="rounded-2xl"
              disabled={deleteMutation.isPending}
              onClick={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
